import { useState } from 'react';

// Tracks dirty/touched state for a flat form object. `set` updates a field and
// marks the form dirty + the field touched; `blur` marks touched only. `touchAll`
// is useful on submit to surface all errors at once. `reset` clears state and
// optionally replaces the values (e.g. after a successful save returns new data).
export function useFormState<T extends Record<string, unknown>>(initial: T) {
  const [values, setValues] = useState<T>(initial);
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});
  const [dirty, setDirty] = useState(false);

  function set<K extends keyof T>(field: K, value: T[K]) {
    setValues(prev => ({ ...prev, [field]: value }));
    setTouched(prev => ({ ...prev, [field]: true }));
    setDirty(true);
  }

  function blur<K extends keyof T>(field: K) {
    setTouched(prev => ({ ...prev, [field]: true }));
  }

  function touchAll() {
    setTouched(prev => {
      const all = { ...prev };
      for (const k of Object.keys(values)) {
        all[k as keyof T] = true;
      }
      return all;
    });
  }

  function reset(next?: T) {
    setValues(next ?? initial);
    setTouched({});
    setDirty(false);
  }

  return { values, set, blur, touched, dirty, touchAll, reset };
}
